import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat, Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1455165814004-1126a7199f9b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.role === "shop_floor" ? "/clock" : "/dashboard");
    } catch (e2) {
      setErr(apiErr(e2, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background grain">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Steel fabrication workshop" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="absolute bottom-0 p-12 z-10">
          <h2 className="font-heading text-4xl font-extrabold uppercase tracking-tight leading-tight">
            One job number.<br />From order to invoice.
          </h2>
          <p className="text-muted-foreground mt-3 max-w-md">
            Production, labour, materials, purchasing and billing for MK Engineering — Rathcairn &amp; Athboy.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <HardHat className="w-7 h-7 text-primary-foreground" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-2xl tracking-wide leading-none">MK ENGINEERING</div>
              <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Job &amp; Production System</div>
            </div>
          </div>

          <h1 className="font-heading text-xl font-bold mb-1">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-6">Use the account created for you by the office.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email" className="mt-1" placeholder="you@mkeng.ie" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password" className="mt-1" placeholder="••••••••" />
            </div>
            {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full font-bold uppercase tracking-wide">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="forgot-link" className="text-sm text-muted-foreground hover:text-primary">
              Forgot password?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
